import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { adminApi } from "../../lib/portalApi";
import { toast } from "sonner";
import { Save, Globe } from "lucide-react";

export default function SeoTab() {
  const [pages, setPages] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [active, setActive] = useState(null);

  const load = async () => {
    const r = await adminApi.seoList();
    const ps = r.pages || [];
    setPages(ps);
    const d = {};
    ps.forEach(p => {
      d[p.page_key] = {
        title: p.title || "",
        description: p.description || "",
        keywords: (p.keywords || []).join(", "),
        og_image: p.og_image || "",
        index: p.index !== false,
        canonical: p.canonical || "",
      };
    });
    setDrafts(d);
    if (!active && ps.length) setActive(ps[0].page_key);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (pk) => {
    const d = drafts[pk];
    try {
      await adminApi.seoPut(pk, {
        title: d.title,
        description: d.description,
        keywords: d.keywords.split(",").map(k => k.trim()).filter(Boolean),
        og_image: d.og_image,
        index: !!d.index,
        canonical: d.canonical,
      });
      toast.success(`SEO updated for ${pk}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const upd = (pk, k, v) => setDrafts({ ...drafts, [pk]: { ...drafts[pk], [k]: v } });
  const d = active ? drafts[active] : null;

  return (
    <div className="space-y-4" data-testid="admin-seo-tab">
      <div className="flex gap-2 flex-wrap">
        {pages.map(p => (
          <button key={p.page_key} onClick={() => setActive(p.page_key)}
            className={`text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded border transition-colors ${
              active === p.page_key
                ? "bg-navy-soft text-gold border-gold"
                : "text-cream-dim border-gold-dim/30 hover:text-gold"
            }`}
            data-testid={`seo-page-${p.page_key}`}>
            {p.page_key}
          </button>
        ))}
      </div>

      {d && active && (
        <div className="glass p-5 space-y-3" data-testid="seo-editor">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-electric">
            <Globe size={12} /> /{active}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.22em] text-steel">Title ({(d.title || "").length}/70)</label>
            <Input value={d.title} onChange={e => upd(active, "title", e.target.value)}
              className="bg-navy-deep border-gold-dim/40 mt-1" data-testid="seo-title" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.22em] text-steel">Description ({(d.description || "").length}/160)</label>
            <Textarea rows={2} value={d.description} onChange={e => upd(active, "description", e.target.value)}
              className="bg-navy-deep border-gold-dim/40 mt-1 text-cream" data-testid="seo-description" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.22em] text-steel">Keywords (comma separated)</label>
            <Input value={d.keywords} onChange={e => upd(active, "keywords", e.target.value)}
              className="bg-navy-deep border-gold-dim/40 mt-1" data-testid="seo-keywords" />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-steel">OG Image URL</label>
              <Input value={d.og_image} onChange={e => upd(active, "og_image", e.target.value)}
                className="bg-navy-deep border-gold-dim/40 mt-1" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-steel">Canonical URL</label>
              <Input value={d.canonical} onChange={e => upd(active, "canonical", e.target.value)}
                className="bg-navy-deep border-gold-dim/40 mt-1" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-cream text-sm">
            <input type="checkbox" checked={!!d.index} onChange={e => upd(active, "index", e.target.checked)}
              data-testid="seo-index" />
            Allow search engine indexing
          </label>
          <div className="flex justify-end">
            <Button onClick={() => save(active)} className="btn-gold h-10 px-5 text-xs"
              data-testid="seo-save-btn">
              <Save size={14} className="mr-2" /> Save SEO
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
